import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Connects native push events to the running app. Renders nothing.
 *
 * Two gaps this closes, both of which made push look broken even when FCM
 * delivered perfectly:
 *
 *  1. FOREGROUND. Android and iOS only draw a tray notification when the app
 *     is backgrounded. With the app open the payload is handed to JS instead
 *     — and nothing was listening, so it vanished. Anyone testing with the
 *     app in front of them saw nothing at all.
 *  2. TAP. The backend has always attached a `deep_link` to the data payload,
 *     but nothing consumed it, so tapping a notification just resumed the app
 *     wherever it was.
 *
 * Mounted once inside the router (it needs `useNavigate`). On the web both
 * events simply never fire — pushNotifications.js no-ops off-native.
 */
export default function PushBridge() {
  const nav = useNavigate();

  useEffect(() => {
    const onReceived = (e) => {
      const n = e?.detail || {};
      const title = n.title || n.data?.title;
      const body = n.body || n.data?.body;
      if (!title && !body) return;
      const link = n.data?.deep_link;
      toast(title || "Notification", {
        description: body,
        // Only offer the jump when there's somewhere to go.
        action: link ? { label: "View", onClick: () => nav(link) } : undefined,
      });
    };

    const onNavigate = (e) => {
      const link = e?.detail?.deep_link;
      // Guard against anything but an in-app path — a notification payload is
      // attacker-influenced in principle, and nav() with an absolute URL
      // would be an open redirect.
      if (typeof link === "string" && link.startsWith("/")) nav(link);
    };

    window.addEventListener("push:received", onReceived);
    window.addEventListener("push:navigate", onNavigate);
    return () => {
      window.removeEventListener("push:received", onReceived);
      window.removeEventListener("push:navigate", onNavigate);
    };
  }, [nav]);

  return null;
}
